/**
 * Custom Application Error Classes
 * 
 * Domain-specific error types for better error handling and logging.
 * All custom errors extend from these base classes.
 */

/**
 * Base application error class
 * All custom errors should extend from this
 */
export class AppError extends Error {
  public readonly code: string;
  public readonly statusCode: number;
  public readonly isOperational: boolean;

  constructor(
    message: string,
    code: string = 'INTERNAL_ERROR',
    statusCode: number = 500,
    isOperational: boolean = true
  ) {
    super(message);
    this.name = this.constructor.name;
    this.code = code;
    this.statusCode = statusCode;
    this.isOperational = isOperational;
    
    Error.captureStackTrace(this, this.constructor);
  }
}

/**
 * Configuration related errors (missing env vars, invalid config, etc.)
 */
export class ConfigurationError extends AppError {
  constructor(message: string, code: string = 'CONFIG_ERROR') {
    super(message, code, 500, true);
  }
}

/**
 * API related errors (Gemini API failures, network issues, etc.)
 */
export class APIError extends AppError {
  constructor(message: string, code: string = 'API_ERROR', statusCode: number = 502) {
    super(message, code, statusCode, true);
  }
}

/**
 * Media related errors (camera/microphone access, audio processing, etc.)
 */
export class MediaError extends AppError {
  constructor(message: string, code: string = 'MEDIA_ERROR') {
    super(message, code, 400, true);
  }
}

/**
 * Validation related errors (invalid input, malformed data, etc.)
 */
export class ValidationError extends AppError {
  constructor(message: string, code: string = 'VALIDATION_ERROR') {
    super(message, code, 400, true);
  }
}

/**
 * Session related errors (connection failures, disconnection issues, etc.)
 */
export class SessionError extends AppError {
  constructor(message: string, code: string = 'SESSION_ERROR') {
    super(message, code, 500, true);
  }
}
